import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
type BodyInitish = BodyInit | Record<string, unknown> | undefined
type FetchOptions = {
  method?: HttpMethod
  headers?: Record<string, string>
  body?: BodyInitish
  baseUrl?: string
  skipAuth?: boolean
  fetchImpl?: typeof fetch
  timeoutMs?: number
}
export type Account = { userKey: string; email: string; deviceId: string }
type Tokens = { accessToken: string; refreshToken: string; account?: Account }
type SessionStatus = 'signed-out' | 'checking' | 'connected' | 'reconnect-required' | 'unavailable'
const ACCESS_KEY = 'auth.accessToken'
const REFRESH_KEY = 'auth.refreshToken'
const ACCOUNT_KEY = 'auth.account'
const DEVICE_KEY = 'auth.deviceId'
const USER_KEY = 'auth.userKey'
const read = (key: string) => (typeof window === 'undefined' ? null : localStorage.getItem(key))
const accessToken = ref(read(ACCESS_KEY))
const refreshToken = ref(read(REFRESH_KEY))
const account = ref<Account | null>(null)
const sessionStatus = ref<SessionStatus>(refreshToken.value ? 'checking' : 'signed-out')
const reconnectOpen = ref(false)
let refreshPromise: Promise<boolean> | null = null
let checkPromise: Promise<void> | null = null
let lastChecked = 0

function loadAccount() {
  try {
    const value = JSON.parse(read(ACCOUNT_KEY) || 'null') as Account | null
    account.value = value?.userKey && value.deviceId ? value : null
  } catch {
    account.value = null
  }
}
loadAccount()

// Used only to keep existing local data scoped to its previous account. This
// decoded identity never grants server access; the backend verifies every token.
function legacyIdentity(): { userKey: string; deviceId: string } | null {
  try {
    const token = accessToken.value || refreshToken.value
    if (!token) return null
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const decoded = JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '=')))
    const identity = decoded.payload || decoded
    return identity.sub && identity.deviceId
      ? { userKey: identity.sub, deviceId: identity.deviceId }
      : null
  } catch {
    return null
  }
}
const rememberedUserKey = ref(account.value?.userKey || legacyIdentity()?.userKey || read(USER_KEY))
if (typeof window !== 'undefined' && rememberedUserKey.value) {
  localStorage.setItem(USER_KEY, rememberedUserKey.value)
  const deviceId = account.value?.deviceId || legacyIdentity()?.deviceId
  if (deviceId) localStorage.setItem(DEVICE_KEY, deviceId)
}
const hasLocalAccount = computed(
  () => !!rememberedUserKey.value || !!account.value || !!refreshToken.value,
)

function syncTokensFromStorage() {
  accessToken.value = read(ACCESS_KEY)
  refreshToken.value = read(REFRESH_KEY)
  loadAccount()
  rememberedUserKey.value =
    account.value?.userKey || legacyIdentity()?.userKey || rememberedUserKey.value
}

function rememberAccount(value: Account) {
  account.value = value
  rememberedUserKey.value = value.userKey
  localStorage.setItem(USER_KEY, value.userKey)
  localStorage.setItem(ACCOUNT_KEY, JSON.stringify(value))
  localStorage.setItem(DEVICE_KEY, value.deviceId)
}

function setTokens(tokens: Tokens | null) {
  accessToken.value = tokens?.accessToken || null
  refreshToken.value = tokens?.refreshToken || null
  if (typeof window !== 'undefined') {
    for (const [key, value] of [
      [ACCESS_KEY, accessToken.value],
      [REFRESH_KEY, refreshToken.value],
    ]) {
      if (value) localStorage.setItem(key!, value)
      else localStorage.removeItem(key!)
    }
  }
  if (tokens) {
    if (tokens.account) rememberAccount(tokens.account)
    rememberedUserKey.value =
      tokens.account?.userKey || legacyIdentity()?.userKey || rememberedUserKey.value
    sessionStatus.value = 'connected'
    lastChecked = Date.now()
    reconnectOpen.value = false
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('auth:connected'))
  } else sessionStatus.value = hasLocalAccount.value ? 'reconnect-required' : 'signed-out'
}

function expireSession() {
  // Preserve remembered identity and local data while removing unusable tokens.
  setTokens(null)
  sessionStatus.value = 'reconnect-required'
}

async function timedFetch(
  fetchImpl: typeof fetch,
  input: string,
  init: RequestInit = {},
  timeoutMs = 15_000,
) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetchImpl(input, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

async function doRefresh(baseUrl = '', fetchImpl: typeof fetch = fetch): Promise<boolean> {
  syncTokensFromStorage()
  if (refreshPromise) return refreshPromise
  const current = refreshToken.value
  if (!current) {
    expireSession()
    return false
  }
  refreshPromise = (async () => {
    try {
      const res = await timedFetch(fetchImpl, `${baseUrl}/rpc/users/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current }),
      })
      // A logout or another tab's replacement must not be undone by this response.
      syncTokensFromStorage()
      if (refreshToken.value !== current) return sessionStatus.value === 'connected'
      if (res.status === 401) {
        expireSession()
        return false
      }
      if (!res.ok) {
        sessionStatus.value = 'unavailable'
        return false
      }
      const data = (await res.json()) as Tokens
      if (!data.accessToken || !data.refreshToken) {
        sessionStatus.value = 'unavailable'
        return false
      }
      if (
        data.account &&
        rememberedUserKey.value &&
        data.account.userKey !== rememberedUserKey.value
      ) {
        expireSession()
        return false
      }
      setTokens(data)
      return true
    } catch {
      if (read(REFRESH_KEY) === current) sessionStatus.value = 'unavailable'
      return false
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

async function authFetch(input: string, opts: FetchOptions = {}): Promise<Response> {
  const {
    method = 'GET',
    headers = {},
    body,
    baseUrl = '',
    skipAuth = false,
    fetchImpl = fetch,
    timeoutMs,
  } = opts
  syncTokensFromStorage()
  const finalHeaders = { ...headers }
  let finalBody: BodyInit | undefined
  if (body != null) {
    if (typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob)) {
      finalHeaders['Content-Type'] ||= 'application/json'
      finalBody = JSON.stringify(body)
    } else finalBody = body as BodyInit
  }
  if (!skipAuth && accessToken.value) finalHeaders.Authorization = `Bearer ${accessToken.value}`
  const url = input.startsWith('http') ? input : `${baseUrl}${input}`
  const request = (requestHeaders: Record<string, string>) => {
    const init = { method, headers: requestHeaders, body: finalBody }
    return timeoutMs ? timedFetch(fetchImpl, url, init, timeoutMs) : fetchImpl(url, init)
  }
  try {
    let res = await request(finalHeaders)
    if (res.status === 401 && !skipAuth) {
      if ((await doRefresh(baseUrl, fetchImpl)) && accessToken.value) {
        res = await request({ ...finalHeaders, Authorization: `Bearer ${accessToken.value}` })
        if (res.status === 401) expireSession()
      }
    }
    if (!skipAuth && res.status >= 500) sessionStatus.value = 'unavailable'
    return res
  } catch (error) {
    if (
      !skipAuth &&
      sessionStatus.value !== 'reconnect-required' &&
      sessionStatus.value !== 'signed-out'
    )
      sessionStatus.value = 'unavailable'
    throw error
  }
}

async function checkSession(force = false, fetchImpl: typeof fetch = fetch) {
  if (checkPromise) return checkPromise
  syncTokensFromStorage()
  if (!refreshToken.value) return
  if (!force && Date.now() - lastChecked < 60_000) return
  const current = refreshToken.value
  checkPromise = (async () => {
    try {
      const res = await authFetch('/rpc/users/auth/session', { fetchImpl, timeoutMs: 15_000 })
      if (
        res.ok &&
        refreshToken.value &&
        (refreshToken.value === current || sessionStatus.value === 'connected')
      ) {
        const data = (await res.json()) as { account: Account }
        if (rememberedUserKey.value && data.account.userKey !== rememberedUserKey.value) {
          expireSession()
          return
        }
        rememberAccount(data.account)
        const wasConnected = sessionStatus.value === 'connected'
        sessionStatus.value = 'connected'
        reconnectOpen.value = false
        lastChecked = Date.now()
        if (!wasConnected) window.dispatchEvent(new Event('auth:connected'))
      }
    } catch {
      /* authFetch already updates connection state */
    } finally {
      checkPromise = null
    }
  })()
  return checkPromise
}

function getDeviceId() {
  const existing = account.value?.deviceId || legacyIdentity()?.deviceId || read(DEVICE_KEY)
  const value = existing || uuidv4()
  localStorage.setItem(DEVICE_KEY, value)
  return value
}

function acceptVerification(tokens: Tokens) {
  if (
    !tokens.account ||
    (rememberedUserKey.value && tokens.account.userKey !== rememberedUserKey.value)
  ) {
    throw new Error('Please reconnect the same account associated with this local timeline.')
  }
  setTokens(tokens)
}

async function logout() {
  syncTokensFromStorage()
  const current = refreshToken.value
  if (current) {
    // Do not claim server logout succeeded if the server cannot be reached.
    const res = await timedFetch(fetch, '/rpc/users/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: current }),
    })
    if (!res.ok) throw new Error('Could not sign out on the server. Please retry when connected.')
    if (read(REFRESH_KEY) !== current)
      throw new Error('The session changed in another tab. Please retry signing out.')
  }
  setTokens(null)
  sessionStatus.value = 'signed-out'
  reconnectOpen.value = false
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (![ACCESS_KEY, REFRESH_KEY, ACCOUNT_KEY, null].includes(event.key)) return
    syncTokensFromStorage()
    sessionStatus.value = refreshToken.value
      ? 'checking'
      : hasLocalAccount.value
        ? 'reconnect-required'
        : 'signed-out'
    if (refreshToken.value) void checkSession(true)
  })
}

export function useAuthClient() {
  return {
    accessToken,
    refreshToken,
    account,
    rememberedUserKey,
    hasLocalAccount,
    sessionStatus,
    reconnectOpen,
    isAuthenticated: computed(() => sessionStatus.value === 'connected'),
    setTokens,
    acceptVerification,
    logout,
    checkSession,
    getDeviceId,
    fetch: authFetch,
    get: (path: string, opts: Omit<FetchOptions, 'method'> = {}) =>
      authFetch(path, { ...opts, method: 'GET' }),
    post: (path: string, body?: BodyInitish, opts: Omit<FetchOptions, 'method' | 'body'> = {}) =>
      authFetch(path, { ...opts, method: 'POST', body }),
    put: (path: string, body?: BodyInitish, opts: Omit<FetchOptions, 'method' | 'body'> = {}) =>
      authFetch(path, { ...opts, method: 'PUT', body }),
    patch: (path: string, body?: BodyInitish, opts: Omit<FetchOptions, 'method' | 'body'> = {}) =>
      authFetch(path, { ...opts, method: 'PATCH', body }),
    del: (path: string, opts: Omit<FetchOptions, 'method'> = {}) =>
      authFetch(path, { ...opts, method: 'DELETE' }),
  }
}
